import apiClient from '@/lib/api-client';
import type { PlatformUser } from '@/types/auth';

export interface PlatformLoginResult {
  accessToken: string;
  platformAdmin: { id: number; email: string; firstName?: string; lastName?: string };
}

export interface ImpersonateResult {
  accessToken: string;
  sessionId: number;
  /** ISO time at which the impersonation token stops working. */
  expiresAt?: string;
  user: { id: number; email: string; role: string; organizationId: number };
}

/** A password step that needs a second step: either a code (mfaToken) or a first enrollment. */
export type PlatformLoginOutcome =
  | ({ mfaRequired: false } & PlatformLoginResult)
  | { mfaRequired: true; mfaToken: string };

export interface MfaSetup {
  secret: string;
  otpauthUri: string;
}

// No token: the interceptor attaches the signed-in platform token.
const bearer = (token: string | null) => (token ? [{ headers: { Authorization: `Bearer ${token}` } }] : []);

function toSession(d: any): PlatformLoginResult {
  return {
    accessToken: d.access_token,
    platformAdmin: {
      id: d.platform_admin.id,
      email: d.platform_admin.email,
      firstName: d.platform_admin.first_name,
      lastName: d.platform_admin.last_name,
    },
  };
}

class PlatformService {
  async logout(): Promise<void> {
    try {
      await apiClient.post('/v1/platform/auth/logout');
    } catch {
      // Stateless endpoint — ignore failures, the client clears its token anyway.
    }
  }

  async login(email: string, password: string): Promise<PlatformLoginOutcome> {
    const { data } = await apiClient.post<any>('/v1/platform/auth/login', { email, password });
    const d = data.data;
    if (d.mfa_required) return { mfaRequired: true, mfaToken: d.mfa_token };
    return { mfaRequired: false, ...toSession(d) };
  }

  async verifyMfa(mfaToken: string, code: string): Promise<PlatformLoginResult> {
    const { data } = await apiClient.post<any>('/v1/platform/auth/mfa/verify', { mfa_token: mfaToken, code });
    return toSession(data.data);
  }

  // Setup and confirm authenticate with the enrollment token from the 403 (there is no session yet);
  // the backend asks for the current password only with a platform token, and ignores it otherwise.
  async setupMfa(token: string | null, password: string): Promise<MfaSetup> {
    const { data } = await apiClient.post<any>('/v1/platform/auth/mfa/setup', { password }, ...bearer(token));
    return { secret: data.data.secret, otpauthUri: data.data.otpauth_uri };
  }

  async confirmMfa(token: string, code: string, password: string): Promise<PlatformLoginResult> {
    const { data } = await apiClient.post<any>('/v1/platform/auth/mfa/confirm', { code, password }, ...bearer(token));
    return toSession(data.data);
  }

  // A signed-in admin turning the second factor on: the password is required (step-up).
  async confirmMfaForSession(code: string, password: string): Promise<void> {
    await apiClient.post('/v1/platform/auth/mfa/confirm', { code, password });
  }

  async listUsers(query = '', page = 1): Promise<{ users: PlatformUser[]; totalPages: number; currentPage: number }> {
    const { data } = await apiClient.get<any>('/v1/platform/users', { params: { q: query, page } });
    return {
      users: (data.data || []).map((u: any) => ({
        id: u.id,
        email: u.email,
        firstName: u.first_name,
        lastName: u.last_name,
        role: u.role,
        active: u.active,
        organizationId: u.organization_id,
        organizationName: u.organization_name,
      })),
      totalPages: data.meta?.total_pages ?? 1,
      currentPage: data.meta?.current_page ?? 1,
    };
  }

  async impersonate(userId: number, reason?: string): Promise<ImpersonateResult> {
    const { data } = await apiClient.post<any>(`/v1/platform/users/${userId}/impersonate`, reason ? { reason } : {});
    const d = data.data;
    return {
      accessToken: d.access_token,
      sessionId: d.impersonation_session_id,
      expiresAt: d.expires_at,
      user: {
        id: d.user.id,
        email: d.user.email,
        role: d.user.role,
        organizationId: d.user.organization_id,
      },
    };
  }

  // Stop must authenticate as the platform admin, whose token is stashed during
  // impersonation — send it explicitly rather than the active (impersonation) token.
  async stopImpersonation(sessionId: number, platformToken: string): Promise<void> {
    await apiClient.post(
      `/v1/platform/impersonations/${sessionId}/stop`,
      {},
      { headers: { Authorization: `Bearer ${platformToken}` } }
    );
  }
}

export const platformService = new PlatformService();
